import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardAPI, usersAPI } from '@/lib/api';
import { prefetchUserProfile } from '@/lib/resources';
import { getCached, setCached, isFresh, swrFetch } from '@/lib/dataCache';
import {
    Trophy, Loader2, Clock, AlertTriangle, XCircle, RotateCcw,
    ChevronDown, ChevronRight, Users, Wallet, Search, ArrowLeft, ArrowUpDown, UserRound,
    Mail, Phone, PackageCheck, Hash
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CommissionsPendingWidget } from '@/components/dashboard/CommissionsPendingWidget';
import { HashtagFilter } from '@/components/prospect/HashtagFilter';
import { HashtagChips } from '@/components/prospect/HashtagChips';

const euro = (n) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '—'; }
};

const initials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

// ---- Avatar chip ----
function PersonChip({ person, active, badge, onClick, testid }) {
    if (!person) return null;
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`group flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 transition-all
                ${active ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-300' : 'border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'}`}
        >
            <Avatar className="h-7 w-7 border border-slate-200">
                {person.avatar && <AvatarImage src={person.avatar} alt={person.name} />}
                <AvatarFallback className="bg-purple-100 text-purple-700 text-[11px] font-semibold">
                    {initials(person.name)}
                </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-slate-700 max-w-[140px] truncate">{person.name}</span>
            {typeof badge === 'number' && badge > 0 && (
                <span className="text-[11px] font-semibold text-purple-700 bg-purple-100 rounded-full px-1.5 py-0.5">{badge}</span>
            )}
        </button>
    );
}

// ---- Bouton "Voir la fiche" (navigue vers le profil équipe + précharge au survol) ----
function FicheButton({ onClick, testid, userId }) {
    return (
        <button type="button" data-testid={testid} onClick={onClick}
            onMouseEnter={() => userId && prefetchUserProfile(userId)} title="Voir la fiche"
            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 transition-all shrink-0">
            <UserRound className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Fiche</span>
        </button>
    );
}

// ---- Per-person stats (gagné / à chercher / perdu) for the teams ranking ----
function PersonStats({ stats }) {
    const s = stats || {};
    const won = s.won || 0;
    const dossiers = s.dossiers || 0;
    const blocks = [
        { label: 'Gagné', value: s.won_amount || 0, color: 'text-emerald-600', testid: 'stat-won' },
        { label: 'À chercher', value: s.pending_amount || 0, color: 'text-blue-600', testid: 'stat-pending' },
        { label: 'Perdu', value: s.lost_amount || 0, color: 'text-rose-500', testid: 'stat-lost' },
    ];
    return (
        <span className="flex items-center justify-end gap-3 sm:gap-5">
            <span className="text-[11px] text-slate-400 whitespace-nowrap hidden md:inline">
                {dossiers} dossier{dossiers > 1 ? 's' : ''} · {won} gagné{won > 1 ? 's' : ''}
            </span>
            {blocks.map((b) => (
                <span key={b.label} className="text-right leading-tight" data-testid={b.testid}>
                    <span className="block text-[9px] uppercase tracking-wide text-slate-400">{b.label}</span>
                    <span className={`block text-xs sm:text-sm font-bold ${b.color}`}>{euro(b.value)}</span>
                </span>
            ))}
        </span>
    );
}

// ---- Summary metric card ----
const BUCKET_META = {
    won: { label: 'Gagnés', icon: Trophy, color: 'emerald' },
    awaiting_install: { label: "En attente d'installation", icon: PackageCheck, color: 'indigo' },
    pending: { label: 'En attente', icon: Wallet, color: 'blue' },
    in_progress: { label: 'En cours', icon: Clock, color: 'blue' },
    overdue: { label: 'En retard', icon: AlertTriangle, color: 'amber' },
    lost: { label: 'Perdus', icon: XCircle, color: 'rose' },
    auto_lost: { label: 'Perdus (inactivité)', icon: RotateCcw, color: 'red' },
};
const COLOR_CLASSES = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    red: 'bg-red-50 text-red-700 border-red-200',
};

function SummaryCard({ bucketKey, value, sub, activeBucket, onClick, hasDetail }) {
    const meta = BUCKET_META[bucketKey];
    const Icon = meta.icon;
    const isActive = activeBucket === bucketKey;
    return (
        <button
            type="button"
            data-testid={`summary-card-${bucketKey}`}
            onClick={onClick}
            disabled={!hasDetail}
            className={`flex flex-col items-start gap-1 rounded-2xl border p-4 text-left transition-all
                ${COLOR_CLASSES[meta.color]}
                ${hasDetail ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'cursor-default'}
                ${isActive ? 'ring-2 ring-offset-1 ring-purple-400 shadow-md' : ''}`}
        >
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">{meta.label}</span>
            </div>
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {sub != null && <span className="text-xs font-medium opacity-80">{sub}</span>}
        </button>
    );
}

// ---- Dossier line card ----
const DossierCard = memo(function DossierCard({ d, onOpen, onTagClick }) {
    const isEP = d.pipeline === 'easy_power';
    return (
        <button
            type="button"
            data-testid={`dossier-card-${d.id}-${d.pipeline}`}
            onClick={() => onOpen(d.id)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-purple-300 hover:shadow-sm transition-all"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 truncate">{d.company_name}</span>
                        <Badge className={`shrink-0 ${isEP ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'} hover:opacity-90`}>
                            {isEP ? 'Easy Power' : 'Chantier'}
                        </Badge>
                    </div>
                    {d.contact_name && d.contact_name !== d.company_name && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{d.contact_name}</p>
                    )}
                    <HashtagChips ids={d.hashtags} max={3} className="mt-1" onTagClick={onTagClick} />
                </div>
                {Array.isArray(d.commissions) && d.commissions.length > 0 && (
                    <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`dossier-commissions-${d.id}`}>
                        {d.commissions.map((c, i) => (
                            <div key={i} className="text-right leading-tight">
                                <p className="text-[10px] uppercase tracking-wide text-slate-400">{c.label}</p>
                                <p className="text-sm font-bold text-slate-800">{euro(c.value)}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1">
                    <span className="font-medium text-slate-700">Étape :</span> {d.stage_label}
                    {d.days_in_stage != null && <span className="text-slate-400">· depuis {d.days_in_stage} j</span>}
                </span>
                <span><span className="font-medium text-slate-700">Créé :</span> {fmtDate(d.created_at)}</span>
                {isEP && <span><span className="font-medium text-slate-700">Étude :</span> {fmtDate(d.etude_recue_at)}</span>}
                {d.bucket === 'overdue' && d.overdue_days != null && (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                        <AlertTriangle className="h-3 w-3" /> Retard de {d.overdue_days} j
                    </span>
                )}
            </div>

            {(d.bucket === 'lost' || d.bucket === 'auto_lost') && d.loss_reason && (
                <p className="mt-2 text-xs text-rose-700 bg-rose-50 rounded-md px-2 py-1">
                    <span className="font-semibold">Raison :</span> {d.loss_reason}
                </p>
            )}

            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                {d.apporteur && (
                    <span className="inline-flex items-center gap-1.5">
                        <Avatar className="h-5 w-5">
                            {d.apporteur.avatar && <AvatarImage src={d.apporteur.avatar} />}
                            <AvatarFallback className="bg-slate-100 text-slate-600 text-[9px]">{initials(d.apporteur.name)}</AvatarFallback>
                        </Avatar>
                        {d.apporteur.name}
                    </span>
                )}
                {d.vp && <span className="text-slate-400">VP : {d.vp.name}</span>}
            </div>
        </button>
    );
});

// ---- En-tête profil (issue 3) : photo, email, téléphone d'un apporteur / VP ciblé ----
function ScopeProfileHeader({ profile, onOpenFiche }) {
    if (!profile) return null;
    const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    const roleLabel = profile.role === 'vp' ? 'VP' : profile.role === 'apporteur' ? 'Apporteur' : profile.role;
    return (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="scope-profile-header">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <Avatar className="h-14 w-14 border border-purple-200">
                    {profile.profile_picture && <AvatarImage src={profile.profile_picture} alt={name} />}
                    <AvatarFallback className="bg-purple-100 text-purple-700 text-lg font-semibold">{initials(name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900 truncate" data-testid="scope-profile-name">{name}</h3>
                        <Badge className="bg-purple-100 text-purple-700 hover:opacity-90">{roleLabel}</Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                        {profile.email && (
                            <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-1.5 hover:text-purple-700 transition-colors" data-testid="scope-profile-email">
                                <Mail className="h-3.5 w-3.5 text-slate-400" /> {profile.email}
                            </a>
                        )}
                        {profile.phone && (
                            <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-1.5 hover:text-purple-700 transition-colors" data-testid="scope-profile-phone">
                                <Phone className="h-3.5 w-3.5 text-slate-400" /> {profile.phone}
                            </a>
                        )}
                        {!profile.email && !profile.phone && (
                            <span className="text-slate-400 italic">Coordonnées non renseignées</span>
                        )}
                    </div>
                </div>
                <Button
                    variant="outline" size="sm" data-testid="scope-profile-fiche-btn"
                    onClick={onOpenFiche}
                    onMouseEnter={() => prefetchUserProfile(profile.id)}
                    className="ml-auto gap-1.5 text-purple-700 border-purple-200 hover:bg-purple-50">
                    <UserRound className="h-4 w-4" /> Voir la fiche complète
                </Button>
            </CardContent>
        </Card>
    );
}

const PERIOD_TYPES = [
    { value: 'all', label: 'Toutes périodes' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: 'Hier' },
    { value: 'this_week', label: 'Cette semaine' },
    { value: 'this_month', label: 'Ce mois-ci' },
    { value: 'this_year', label: 'Cette année' },
    { value: 'custom', label: 'Période personnalisée' },
];

const PIPELINE_TYPES = [
    { value: 'all', label: 'Tous les modules' },
    { value: 'easy_power', label: 'Easy Power' },
    { value: 'chantier', label: 'Chantier' },
];

// Tri intelligent des listes de dossiers (vue détail). Le montant respecte le rôle
// connecté (champ `sort_amount` calculé côté backend) et tous les filtres actifs.
const SORT_OPTIONS = [
    { value: 'amount_desc', label: 'Montant ↓ (plus gros)' },
    { value: 'amount_asc', label: 'Montant ↑ (plus petit)' },
    { value: 'overdue_desc', label: 'Retard le + élevé' },
    { value: 'created_desc', label: 'Création (récent)' },
    { value: 'created_asc', label: 'Création (ancien)' },
    { value: 'activity_desc', label: 'Dernière activité' },
    { value: 'lost_date', label: 'Date passage perdu' },
    { value: 'stage', label: 'Étape pipeline' },
    { value: 'apporteur', label: 'Apporteur' },
    { value: 'team', label: 'Équipe' },
    { value: 'module', label: 'Module' },
];

const _s = (v) => v || '';
function sortDossiers(rows, key) {
    const r = [...(rows || [])];
    switch (key) {
        case 'amount_desc': r.sort((a, b) => (b.sort_amount || 0) - (a.sort_amount || 0)); break;
        case 'amount_asc': r.sort((a, b) => (a.sort_amount || 0) - (b.sort_amount || 0)); break;
        case 'overdue_desc': r.sort((a, b) => (b.overdue_days || 0) - (a.overdue_days || 0)); break;
        case 'created_desc': r.sort((a, b) => _s(b.created_at).localeCompare(_s(a.created_at))); break;
        case 'created_asc': r.sort((a, b) => _s(a.created_at).localeCompare(_s(b.created_at))); break;
        case 'activity_desc': r.sort((a, b) => _s(b.updated_at).localeCompare(_s(a.updated_at))); break;
        case 'lost_date': r.sort((a, b) => _s(b.stage_changed_at).localeCompare(_s(a.stage_changed_at))); break;
        case 'stage': r.sort((a, b) => _s(a.stage_label).localeCompare(_s(b.stage_label))); break;
        case 'apporteur': r.sort((a, b) => _s(a.apporteur?.name).localeCompare(_s(b.apporteur?.name))); break;
        case 'team': r.sort((a, b) => _s(a.team_name).localeCompare(_s(b.team_name))); break;
        case 'module': r.sort((a, b) => _s(a.pipeline).localeCompare(_s(b.pipeline))); break;
        default: break;
    }
    return r;
}

export default function PilotageDashboard({ user }) {
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);
    const dataRef = useRef(false); // a-t-on déjà des données à l'écran ?
    const dataKeyRef = useRef(null); // clé (cible) des données actuellement affichées
    const autoPickRef = useRef(false); // autoriser l'auto-sélection du 1er bucket non vide (entrée scope)
    const detailRef = useRef(null); // pour scroller vers la liste au clic KPI
    const [expandedTeams, setExpandedTeams] = useState({});

    // ============================================================
    // ÉTAT DE VUE = URL (source de vérité) : chaque niveau de drill-down
    // (scope, bucket, filtres) est encodé dans l'URL → vrai historique de
    // navigation (Retour/Suivant) + restauration exacte du contexte.
    // ============================================================
    const [searchParams, setSearchParams] = useSearchParams();

    const periodType = searchParams.get('period') || 'all';
    const monthVal = searchParams.get('month') || '';
    const yearVal = searchParams.get('year') || String(new Date().getFullYear());
    const weekStart = searchParams.get('week_start') || '';
    const dateFrom = searchParams.get('from') || '';
    const dateTo = searchParams.get('to') || '';
    const pipeline = searchParams.get('pipeline') || 'all';
    const scopeType = searchParams.get('scope_type') || 'all';
    const scopeId = searchParams.get('scope_id') || null;
    const scopeName = searchParams.get('scope_name') || null;
    const scope = useMemo(() => ({ type: scopeType, id: scopeId, name: scopeName }), [scopeType, scopeId, scopeName]);
    const forceDetail = searchParams.get('detail') === '1';
    const activeBucket = searchParams.get('bucket') || 'overdue';
    const sortBy = searchParams.get('sort') || 'amount_desc';

    // Met à jour les paramètres d'URL. push=true → nouvelle entrée d'historique
    // (drill-down navigable au bouton Retour) ; sinon replace (raffinement de filtre).
    const patchParams = useCallback((updates, { push = false } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === undefined || v === '' || v === false) next.delete(k);
            else next.set(k, v === true ? '1' : String(v));
        });
        setSearchParams(next, { replace: !push });
    }, [searchParams, setSearchParams]);

    const buildParams = useCallback(() => {
        const p = { period: periodType, pipeline, scope_type: scope.type };
        if (scope.id) p.scope_id = scope.id;
        if (periodType === 'month') p.month = monthVal;
        if (periodType === 'year') p.year = yearVal;
        if (periodType === 'week') p.week_start = weekStart;
        if (periodType === 'custom') {
            if (dateFrom) p.date_from = dateFrom;
            if (dateTo) p.date_to = dateTo;
        }
        if (forceDetail) p.detail = true;
        return p;
    }, [periodType, pipeline, scope, monthVal, yearVal, weekStart, dateFrom, dateTo, forceDetail]);

    const load = useCallback(async () => {
        // For specific period types, wait until the value is provided
        if (periodType === 'month' && !monthVal) { setLoading(false); return; }
        if (periodType === 'week' && !weekStart) { setLoading(false); return; }
        if (periodType === 'custom' && !dateFrom && !dateTo) { setLoading(false); return; }
        const params = buildParams();
        const cacheKey = `pilotage:${user?.id || 'anon'}:${JSON.stringify(params)}`;

        // 1) Hydratation INSTANTANÉE depuis le cache mémoire (Map) — zéro coût de parsing,
        //    survit aux démontages/remontages du composant pendant la session SPA.
        //    => le bouton "Retour" réaffiche l'écran immédiatement.
        let hadFresh = false;
        let hydrated = false;
        const mem = getCached(cacheKey);
        if (mem !== undefined) {
            setData(mem);
            dataRef.current = true;
            dataKeyRef.current = cacheKey;
            setLoading(false);
            setError(false);
            hadFresh = isFresh(cacheKey);
            hydrated = true;
        } else {
            // Fallback persistant (rechargement de page) : parse localStorage UNE fois puis
            //  on réchauffe le cache mémoire pour les navigations suivantes.
            try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    setData(parsed);
                    setCached(cacheKey, parsed);
                    dataRef.current = true;
                    dataKeyRef.current = cacheKey;
                    setLoading(false);
                    setError(false);
                    hydrated = true;
                }
            } catch { /* cache illisible : on ignore */ }
        }

        // 2) Cache mémoire FRAIS (< TTL) → on NE refetch PAS : retour réellement instantané,
        //    sans second rendu provoqué par la réponse réseau.
        if (hadFresh) return;

        // 3) État de chargement :
        //    - Nouvelle cible sans cache → on efface l'écran précédent pour afficher des
        //      SKELETONS (et non l'ancien écran / un écran vide qui donne l'impression que "rien ne s'affiche").
        //    - Sinon (mêmes données déjà à l'écran) → refresh discret.
        setError(false);
        if (!hydrated && dataKeyRef.current !== cacheKey) {
            setData(null);
            dataRef.current = false;
            setLoading(true);
        } else if (dataRef.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        try {
            const res = await dashboardAPI.getPilotage(params);
            setData(res.data);
            setCached(cacheKey, res.data);
            dataRef.current = true;
            dataKeyRef.current = cacheKey;
            try { localStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch { /* quota plein : on ignore */ }
        } catch (e) {
            console.error('Pilotage load error', e);
            setError(true); // message + bouton Réessayer plutôt qu'un spinner sans fin
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildParams, periodType, monthVal, weekStart, dateFrom, dateTo, user?.id]);

    useEffect(() => {
        // Debounce léger SEULEMENT pour le réseau : l'hydratation cache est immédiate dans load().
        const t = setTimeout(() => { load(); }, 120);
        return () => clearTimeout(t);
    }, [load]);

    const summary = data?.summary || {};
    const teams = data?.teams || [];
    const buckets = data?.buckets || {};
    const hasDetail = !!data?.has_detail;

    // Label financier 'Niveau 1' selon le rôle (un VP peut être payeur ET superviseur → cumulé)
    const isSupervisor = teams.some((t) => t.supervisor_vp?.id === user?.id);
    const isPayer = teams.some((t) => t.payer_vp?.id === user?.id);
    const financeLabel = isAdmin
        ? 'Mon résultat Valnexo'
        : user?.role === 'vp'
            ? (isSupervisor && isPayer ? 'Ma part (VP + supervision)' : isSupervisor ? 'Ma part superviseur' : 'Ma part VP')
            : 'Ma commission';
    // Montants 'ma part' (fallback sur les totaux si l'API ne les renvoie pas encore)
    const myWon = summary.my_won_amount ?? summary.won_amount;
    const myPending = summary.my_pending_amount ?? summary.pending_amount;
    const myOverdue = summary.my_overdue_amount ?? summary.overdue_amount;

    // Lignes du bucket actif, triées selon le critère choisi.
    // 'pending' = vue combinée « En attente » = en cours + en retard (correspond au cadre financier).
    // useMemo : le tri (potentiellement lourd) ne se recalcule que si les données ou le tri changent
    // — évite de re-trier à chaque rendu (frappe, hover, etc.) et fluidifie le « Retour ».
    const activeRows = useMemo(() => (
        activeBucket === 'pending'
            ? [...(buckets.in_progress || []), ...(buckets.overdue || []), ...(buckets.awaiting_install || [])]
            : (buckets[activeBucket] || [])
    ), [activeBucket, buckets]);
    const [hashtagFilter, setHashtagFilter] = useState([]);
    const detailRows = useMemo(() => {
        let rows = sortDossiers(activeRows, sortBy);
        if (hashtagFilter.length > 0) {
            rows = rows.filter((d) => (d.hashtags || []).some((id) => hashtagFilter.includes(id)));
        }
        return rows;
    }, [activeRows, sortBy, hashtagFilter]);
    const activeBucketCount = activeBucket === 'pending'
        ? (summary.in_progress ?? 0) + (summary.overdue ?? 0) + (summary.awaiting_install ?? 0)
        : (summary[activeBucket] ?? 0);

    // Tri des membres d'équipe précalculé une seule fois par jeu de données (vue d'ensemble).
    const sortedTeams = useMemo(() => teams.map((t) => ({
        ...t,
        sortedMembers: [...(t.members || [])].sort(
            (a, b) => (b.stats?.won_amount || 0) - (a.stats?.won_amount || 0)
                || (b.stats?.pending_amount || 0) - (a.stats?.pending_amount || 0)
        ),
    })), [teams]);

    // ============================================================
    // Issue 3 — En-tête profil quand on cible un apporteur / VP :
    // photo, email, téléphone (vue management « 360° »). Chargé depuis le
    // cache (instantané si déjà préchargé au survol via la fiche).
    // ============================================================
    const [scopeProfile, setScopeProfile] = useState(null);
    useEffect(() => {
        const isUserScope = (scope.type === 'apporteur' || scope.type === 'vp') && scope.id;
        if (!isUserScope) { setScopeProfile(null); return; }
        let cancelled = false;
        const key = `pilotage-userhdr:${scope.id}`;
        // Affichage instantané si déjà en cache, sinon fetch léger.
        const cached = getCached(key);
        if (cached) setScopeProfile(cached);
        swrFetch(key, async () => (await usersAPI.getProfile(scope.id)).data)
            .then((p) => { if (!cancelled) setScopeProfile(p); })
            .catch(() => { if (!cancelled && !cached) setScopeProfile(null); });
        return () => { cancelled = true; };
    }, [scope.type, scope.id]);

    // ============================================================
    // Rendu progressif de la liste de dossiers : le backend renvoie jusqu'à
    // 300 lignes/bucket. Tout monter d'un coup (300 cartes) bloque le paint et
    // alourdit le « Retour ». On affiche 40 lignes puis on étend au scroll.
    // ============================================================
    const DETAIL_PAGE = 40;
    const [visibleCount, setVisibleCount] = useState(DETAIL_PAGE);
    useEffect(() => { setVisibleCount(DETAIL_PAGE); }, [activeBucket, scope.id, scope.type, sortBy, hashtagFilter]);
    const visibleRows = useMemo(() => detailRows.slice(0, visibleCount), [detailRows, visibleCount]);
    const onDetailScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
            setVisibleCount((c) => (c < detailRows.length ? c + DETAIL_PAGE : c));
        }
    }, [detailRows.length]);

    // Quand on entre dans une CIBLE (clic apporteur/équipe), sélectionner le 1er bucket non vide.
    // On NE touche PAS au bucket choisi explicitement via un clic KPI (« ni plus ni moins »).
    useEffect(() => {
        if (!hasDetail) return;
        if (!autoPickRef.current) return;
        autoPickRef.current = false;
        if ((buckets[activeBucket] || []).length > 0) return;
        const order = ['overdue', 'auto_lost', 'awaiting_install', 'in_progress', 'won', 'lost'];
        const firstNonEmpty = order.find((b) => (buckets[b] || []).length > 0);
        if (firstNonEmpty && firstNonEmpty !== activeBucket) patchParams({ bucket: firstNonEmpty });
    }, [hasDetail, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectScope = (type, id, name) => {
        autoPickRef.current = true; // à l'entrée d'une cible, on peut auto-choisir un bucket non vide
        patchParams({ scope_type: type, scope_id: id, scope_name: name, detail: null, bucket: 'overdue' }, { push: true });
    };
    const resetScope = () => {
        patchParams({ scope_type: null, scope_id: null, scope_name: null, detail: null, bucket: null }, { push: true });
    };
    // Clic sur un KPI : ouvre EXACTEMENT la liste des dossiers de ce cadre (ni plus ni moins),
    // puis scrolle vers la liste.
    const openBucket = (b) => {
        autoPickRef.current = false;
        const updates = { bucket: b };
        if (scope.type === 'all') updates.detail = true;
        patchParams(updates, { push: true });
        setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    };

    const yearOptions = [];
    const cy = new Date().getFullYear();
    for (let y = cy; y >= cy - 6; y--) yearOptions.push(String(y));

    // Callback stable → DossierCard (memo) ne re-render pas inutilement au scroll.
    const openDossier = useCallback((id) => navigate(`/prospects/${id}`), [navigate]);
    // Clic sur une pastille # d'une carte → applique/retire ce # au filtre de la liste.
    const toggleHashtag = useCallback((id) => {
        setHashtagFilter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    return (
        <div className="space-y-6" data-testid="pilotage-dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                        Pilotage, {user?.first_name}
                    </h1>
                    <p className="text-slate-600 mt-1 text-sm">
                        {isAdmin ? "Vue d'ensemble société — équipes, dossiers et montants" : 'Pilotez vos équipes et leurs dossiers'}
                    </p>
                </div>
            </div>

            {/* Filtres */}
            <Card className="border-slate-200">
                <CardContent className="p-4 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500">Période</label>
                        <Select value={periodType} onValueChange={(v) => { patchParams({ period: v === 'all' ? null : v, from: null, to: null }); }}>
                            <SelectTrigger className="w-[180px]" data-testid="period-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PERIOD_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {periodType === 'custom' && (
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-slate-500">Du</label>
                                <input type="date" value={dateFrom} onChange={(e) => patchParams({ from: e.target.value })}
                                    data-testid="date-from-input"
                                    className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-slate-500">Au</label>
                                <input type="date" value={dateTo} onChange={(e) => patchParams({ to: e.target.value })}
                                    data-testid="date-to-input"
                                    className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
                            </div>
                        </>
                    )}

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500">Module</label>
                        <Select value={pipeline} onValueChange={(v) => patchParams({ pipeline: v === 'all' ? null : v })}>
                            <SelectTrigger className="w-[170px]" data-testid="pipeline-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PIPELINE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {(scope.type !== 'all' || forceDetail) && (
                        <Button variant="ghost" size="sm" onClick={resetScope} data-testid="reset-scope-btn" className="gap-1 text-purple-700">
                            <ArrowLeft className="h-4 w-4" /> Retour vue d'ensemble
                        </Button>
                    )}
                    {refreshing && (
                        <span className="flex items-center gap-1.5 text-xs text-slate-400 ml-auto" data-testid="pilotage-refreshing">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Actualisation…
                        </span>
                    )}
                </CardContent>
            </Card>

            {/* Erreur de chargement : message clair + bouton Réessayer (jamais de spinner sans fin) */}
            {error && !loading && !refreshing && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm" data-testid="pilotage-error">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <span className="text-amber-800 flex-1">Impossible de charger les données (délai dépassé). Vérifiez votre connexion puis réessayez.</span>
                    <Button size="sm" variant="outline" onClick={() => load()} className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="pilotage-retry-btn">
                        <RotateCcw className="h-4 w-4" /> Réessayer
                    </Button>
                </div>
            )}

            {/* Synthèse financière par rôle (Niveau 1) — cliquable pour le détail (Niveau 2) */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-purple-600" />
                    <h2 className="text-base font-semibold text-slate-800" data-testid="finance-role-label">{financeLabel}</h2>
                    <span className="text-xs text-slate-400">(selon période &amp; module sélectionnés)</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button type="button" onClick={() => openBucket('won')} data-testid="amount-card-won"
                        className="text-left rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-emerald-700 flex items-center gap-1"><Trophy className="h-4 w-4" /> Gagné</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-won">{loading && !data ? '…' : euro(myWon)}</p>
                    </button>
                    <button type="button" onClick={() => openBucket('pending')} data-testid="amount-card-pending"
                        className="text-left rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-blue-700 flex items-center gap-1"><Wallet className="h-4 w-4" /> En attente</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-pending">{loading && !data ? '…' : euro(myPending)}</p>
                        <p className="text-xs text-slate-500">dossiers en cours à aller chercher</p>
                    </button>
                    <button type="button" onClick={() => openBucket('overdue')} data-testid="amount-card-overdue"
                        className="text-left rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-amber-700 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> En retard</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-overdue">{loading && !data ? '…' : euro(myOverdue)}</p>
                    </button>
                </div>
            </div>

            {/* Compteurs buckets (cliquables) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {['won', 'awaiting_install', 'in_progress', 'overdue', 'lost', 'auto_lost'].map((b) => (
                    <SummaryCard key={b} bucketKey={b} value={loading && !data ? '…' : (summary[b] ?? 0)}
                        activeBucket={hasDetail ? activeBucket : null}
                        hasDetail={true}
                        onClick={() => openBucket(b)} />
                ))}
            </div>

            {/* Scope banner */}
            {hasDetail && (
                <div className="flex items-center gap-2 text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="scope-banner">
                    <Search className="h-4 w-4 text-purple-600" />
                    <span className="text-slate-700">Sélection : <span className="font-semibold text-purple-800">{scope.name || (forceDetail ? 'Toute la société' : scope.type)}</span></span>
                </div>
            )}

            {/* En-tête profil (apporteur / VP ciblé) — vue management 360° */}
            {hasDetail && (scope.type === 'apporteur' || scope.type === 'vp') && scopeProfile && (
                <ScopeProfileHeader profile={scopeProfile} onOpenFiche={() => navigate(`/dashboard/team/user/${scopeProfile.id}`)} />
            )}

            {/* Détail des dossiers (si une cible est sélectionnée) */}
            {hasDetail ? (
                <Card className="border-slate-200" ref={detailRef}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                {['won', 'awaiting_install', 'pending', 'in_progress', 'overdue', 'lost', 'auto_lost'].map((b) => {
                                    const meta = BUCKET_META[b];
                                    const Icon = meta.icon;
                                    const count = b === 'pending'
                                        ? (summary.in_progress ?? 0) + (summary.overdue ?? 0) + (summary.awaiting_install ?? 0)
                                        : (summary[b] ?? (buckets[b] || []).length);
                                    return (
                                        <button key={b} type="button" data-testid={`bucket-tab-${b}`}
                                            onClick={() => { autoPickRef.current = false; patchParams({ bucket: b }, { push: true }); }}
                                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-all
                                                ${activeBucket === b ? 'border-purple-500 bg-purple-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-purple-300'}`}>
                                            <Icon className="h-3.5 w-3.5" /> {meta.label}
                                            <span className={`text-[11px] rounded-full px-1.5 ${activeBucket === b ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Tri intelligent (priorité économique) */}
                            <div className="flex items-center gap-2">
                                <HashtagFilter selected={hashtagFilter} onChange={setHashtagFilter} className="h-9 w-[150px]" />
                                {hashtagFilter.length > 0 && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 whitespace-nowrap" data-testid="hashtag-match-count">
                                        <Hash className="w-3 h-3" />{detailRows.length} dossier{detailRows.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                                <ArrowUpDown className="h-4 w-4 text-slate-400" />
                                <Select value={sortBy} onValueChange={(v) => patchParams({ sort: v === 'amount_desc' ? null : v })}>
                                    <SelectTrigger className="h-9 w-[200px]" data-testid="dossier-sort-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {activeBucket === 'auto_lost' && (buckets.auto_lost || []).length > 0 && (
                            <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                Ces dossiers sont passés automatiquement en perdu après 15 jours d'inactivité sur la même étape (Easy Power).
                                Ouvrez-les pour les réattribuer à un apporteur et les relancer.
                            </div>
                        )}

                        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="dossier-list" onScroll={onDetailScroll}>
                            {loading && !data ? (
                                [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)
                            ) : detailRows.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-8">Aucun dossier dans cette catégorie pour la période sélectionnée.</p>
                            ) : (
                                visibleRows.map((d) => (
                                    <DossierCard key={`${d.id}-${d.pipeline}`} d={d} onOpen={openDossier} onTagClick={toggleHashtag} />
                                ))
                            )}
                            {!loading && visibleCount < detailRows.length && (
                                <p className="text-xs text-slate-400 text-center py-2" data-testid="dossier-load-more-hint">
                                    Affichage de {visibleRows.length} dossiers sur {detailRows.length}. Faites défiler pour en afficher plus.
                                </p>
                            )}
                            {!loading && activeBucketCount > detailRows.length && visibleCount >= detailRows.length && (
                                <p className="text-xs text-slate-400 text-center py-2">
                                    Affichage des {detailRows.length} premiers dossiers sur {activeBucketCount}. Affinez les filtres pour réduire la liste.
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                /* Navigation par équipes */
                <div className="space-y-3" data-testid="teams-nav">
                    <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <Users className="h-5 w-5 text-purple-600" /> Équipes
                    </h2>
                    {loading && !data ? (
                        [1, 2].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)
                    ) : teams.length === 0 ? (
                        <p className="text-sm text-slate-400">Aucune équipe visible.</p>
                    ) : (
                        sortedTeams.map((t) => {
                            const expanded = expandedTeams[t.id] !== false; // default expanded
                            const c = t.counts || {};
                            const total = (c.won || 0) + (c.in_progress || 0) + (c.overdue || 0) + (c.lost || 0) + (c.auto_lost || 0);
                            return (
                                <Card key={t.id} className="border-slate-200 overflow-hidden" data-testid={`team-card-${t.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <button type="button" data-testid={`team-toggle-${t.id}`}
                                                onClick={() => setExpandedTeams((p) => ({ ...p, [t.id]: !expanded }))}
                                                className="flex items-center gap-2 min-w-0">
                                                {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                                                <span className="font-semibold text-slate-900 truncate">{t.name}</span>
                                                <span className="text-xs text-slate-400">({total})</span>
                                            </button>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                {c.overdue > 0 && <Badge className="bg-amber-100 text-amber-700 hover:opacity-90">{c.overdue} retard</Badge>}
                                                {c.auto_lost > 0 && <Badge className="bg-red-100 text-red-700 hover:opacity-90">{c.auto_lost} inactivité</Badge>}
                                                <Button size="sm" variant="outline" data-testid={`team-open-${t.id}`}
                                                    onClick={() => selectScope('team', t.id, t.name)} className="h-7 text-xs">
                                                    Voir l'équipe
                                                </Button>
                                            </div>
                                        </div>

                                        {expanded && (
                                            <div className="mt-3 space-y-3">
                                                <div className="flex flex-wrap gap-3">
                                                    {t.payer_vp && (
                                                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                                            <span className="text-[11px] font-semibold text-slate-400 uppercase">VP</span>
                                                            <PersonChip person={t.payer_vp} testid={`scope-vp-${t.payer_vp.id}`}
                                                                onClick={() => selectScope('vp', t.payer_vp.id, t.payer_vp.name)} />
                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${t.payer_vp.id}`)} userId={t.payer_vp.id} testid={`fiche-vp-${t.payer_vp.id}`} />
                                                            <PersonStats stats={t.payer_vp.stats} />
                                                        </div>
                                                    )}
                                                    {t.supervisor_vp && (
                                                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                                            <span className="text-[11px] font-semibold text-purple-400 uppercase">Superviseur</span>
                                                            <PersonChip person={t.supervisor_vp} testid={`scope-sup-${t.supervisor_vp.id}`}
                                                                onClick={() => selectScope('vp', t.supervisor_vp.id, t.supervisor_vp.name)} />
                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${t.supervisor_vp.id}`)} userId={t.supervisor_vp.id} testid={`fiche-sup-${t.supervisor_vp.id}`} />
                                                            <PersonStats stats={t.supervisor_vp.stats} />
                                                        </div>
                                                    )}
                                                </div>
                                                {t.members.length > 0 ? (
                                                    <div data-testid={`team-ranking-${t.id}`}>
                                                        <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Classement apporteurs</p>
                                                        <div className="space-y-1">
                                                            {t.sortedMembers
                                                                .map((m, idx) => (
                                                                    <div key={m.id}
                                                                        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 rounded-lg border border-slate-100 hover:border-purple-200 hover:bg-purple-50/40 px-2.5 py-1.5 transition-all">
                                                                        <button type="button" data-testid={`scope-member-${m.id}`}
                                                                            onClick={() => selectScope('apporteur', m.id, m.name)}
                                                                            className="flex items-center gap-2 min-w-0 flex-1 text-left">
                                                                            <span className="text-[11px] font-bold text-slate-400 w-4 text-right">{idx + 1}</span>
                                                                            <Avatar className="h-6 w-6 border border-slate-200">
                                                                                {m.avatar && <AvatarImage src={m.avatar} alt={m.name} />}
                                                                                <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px] font-semibold">{initials(m.name)}</AvatarFallback>
                                                                            </Avatar>
                                                                            <span className="text-sm font-medium text-slate-700 truncate">{m.name}</span>
                                                                        </button>
                                                                        <div className="flex items-center justify-end gap-2 shrink-0 w-full sm:w-auto pl-6 sm:pl-0">
                                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${m.id}`)} userId={m.id} testid={`fiche-member-${m.id}`} />
                                                                            <PersonStats stats={m.stats} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                ) : (!t.payer_vp && (
                                                    <span className="text-xs text-slate-400">Aucun membre rattaché.</span>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>
            )}

            {/* Admin : commissions à valider */}
            {isAdmin && (
                <div data-testid="commissions-to-validate">
                    <CommissionsPendingWidget />
                </div>
            )}
        </div>
    );
}
